import { AppButton, AppConfirmDeleteDialog, AppDialog } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import type { InquiryResponse } from 'backend/types'
import { useCallback, useEffect, useMemo } from 'react'
import { useAuthStore } from '@/stores/auth'
import { useInquiryAssigns } from '../../hooks/useInquiryAssigns'
import { useInquiryEditForm } from '../../hooks/useInquiryEditForm'
import { usePropertiesAssignDialogs } from '../../hooks/usePropertiesAssignDialogs'
import { AssignSaveDialog } from './components/AssignSaveDialog'
import { InquiryStatusBadge } from './components/InquiryStatusBadge'
import { useDeleteInquiryAssign } from './hooks/useDeleteInquiryAssign'
import { useInquiryStatusSuggestion } from './hooks/useInquiryStatusSuggestion'
import { AmountDetailsSection } from './sections/AmountDetailsSection'
import { AssignListSection } from './sections/AssignListSection'
import { DEFAULT_CONTRACT_TYPES, type InquiryAssignLocal } from './types'

type AssignTabProps = {
  inquiry: InquiryResponse
  isInquiryCompleted: boolean
  onDirtyChange: (isDirty: boolean) => void
}

export function AssignTab({ inquiry, isInquiryCompleted, onDirtyChange }: AssignTabProps) {
  const user = useAuthStore((state) => state.user)
  // API（canEditInquiryAssign）の営業に対する条件に合わせる。役職者はログイン時に付く administrator で判定するため、
  // ログイン後に役職が付いた人は再ログインまで押せない（API より厳しい側にずれる）
  const hasInquiryUpdate = user?.acl?.inquiry?.update ?? false
  const isAclPrivileged = Boolean(user?.acl?.administrator || user?.acl?.inquiry?.privileged)
  const isRestrictedSales = Boolean(user?.isSalesMember && !user.isServiceManager && !isAclPrivileged)
  const canEditAssign = hasInquiryUpdate && !isRestrictedSales && !isInquiryCompleted
  const { data: assignsData, isLoading: isAssignsLoading, isError: isAssignsError } = useInquiryAssigns(inquiry.id)
  // TODO: contract_types はDB未対応のため暫定でデフォルト値を補完
  // DB移行・API対応後はマッピング不要になり、InquiryAssignResponseに統合される
  const assigns: InquiryAssignLocal[] = useMemo(
    () =>
      (assignsData?.inquiry_assigns ?? []).map((a) => ({
        ...a,
        contract_types: DEFAULT_CONTRACT_TYPES,
        work_locations: {
          onsite: a.is_onsite,
          offsite: a.is_offsite,
          remote: a.is_remote,
        },
      })),
    [assignsData],
  )
  const { formValues, formErrors, isDirty, updateField, resetForm, handleSave, validate } = useInquiryEditForm(inquiry)
  const { mutateAsync: deleteAssign } = useDeleteInquiryAssign(inquiry.id)

  const {
    assignDialog,
    assignDeleteTargetId,
    openAddAssignDialog,
    openEditAssignDialog,
    closeAssignDialog,
    openDeleteAssignDialog,
    closeDeleteAssignDialog,
  } = usePropertiesAssignDialogs()

  const statusSuggestion = useInquiryStatusSuggestion({
    inquiryId: inquiry.id,
    currentInquiryStatusId: inquiry.inquiry_assign_status_id,
    assignsData,
  })

  /**
   * アサインの追加・編集の保存成功時に、引合ステータスの変更提案を評価
   */
  const handleAssignSaveSuccess = useCallback(
    (savedAssignStatusId: number) => {
      statusSuggestion.evaluate(savedAssignStatusId, assignDialog.assign?.id)
    },
    [statusSuggestion.evaluate, assignDialog.assign?.id],
  )

  const totalAssignAmount = useMemo(() => {
    if (assigns.length === 0) return null
    return assigns.reduce((sum, a) => sum + a.unit_price * a.assign_person_month, 0)
  }, [assigns])

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  const handleDeleteAssignConfirm = useCallback(async () => {
    if (assignDeleteTargetId === null) return
    try {
      await deleteAssign(assignDeleteTargetId)
      closeDeleteAssignDialog()
    } catch {}
  }, [assignDeleteTargetId, deleteAssign, closeDeleteAssignDialog])

  return (
    <>
      <div className='flex h-full flex-col'>
        <div className='flex-1 space-y-6 overflow-auto p-4'>
          <AmountDetailsSection
            totalAssignAmount={totalAssignAmount}
            formValues={formValues}
            formErrors={formErrors}
            onFieldChange={updateField}
            isInquiryCompleted={isInquiryCompleted}
          />
          <AssignListSection
            assigns={assigns}
            isLoading={isAssignsLoading}
            isError={isAssignsError}
            canEdit={canEditAssign}
            onAddAssign={openAddAssignDialog}
            onEditAssign={openEditAssignDialog}
            onDeleteAssign={openDeleteAssignDialog}
          />
        </div>

        {/* フッター */}
        <div className='flex items-center justify-end gap-2 border-t px-4 py-3'>
          <AppButton variant='outline' disabled={isInquiryCompleted || !isDirty} onClick={resetForm}>
            キャンセル
          </AppButton>
          <AppButton
            variant='save'
            disabled={isInquiryCompleted || !isDirty}
            onClick={() => {
              if (validate()) {
                handleSave().catch(() => {})
              }
            }}
          >
            保存
          </AppButton>
        </div>
      </div>

      <AssignSaveDialog
        open={assignDialog.open}
        onOpenChange={(open) => {
          if (!open) closeAssignDialog()
        }}
        inquiryId={inquiry.id}
        assign={assignDialog.assign}
        onSaveSuccess={handleAssignSaveSuccess}
      />

      {/* アサイン削除確認ダイアログ */}
      <AppConfirmDeleteDialog
        open={assignDeleteTargetId !== null}
        onOpenChange={(open) => {
          if (!open) closeDeleteAssignDialog()
        }}
        message='このアサインを削除しますか？'
        onConfirm={handleDeleteAssignConfirm}
      />

      {/* 引合ステータス変更提案ダイアログ */}
      <AppDialog
        open={statusSuggestion.suggestedStatusId !== null}
        onOpenChange={(open) => {
          if (!open) statusSuggestion.clear()
        }}
        title='引合ステータスの変更'
        isPending={statusSuggestion.isPending}
        className='sm:max-w-md'
        footerOptions={[
          {
            label: 'いいえ',
            onClick: statusSuggestion.clear,
            variant: 'ghost',
            disabled: statusSuggestion.isPending,
          },
          {
            label: 'はい',
            onClick: statusSuggestion.confirm,
            isPending: statusSuggestion.isPending,
          },
        ]}
      >
        <div className='space-y-4 py-4'>
          <div className='flex flex-wrap items-center gap-2 text-sm'>
            <span>引合ステータスを</span>
            <InquiryStatusBadge statusId={inquiry.inquiry_assign_status_id} />
            <span>から</span>
            <InquiryStatusBadge statusId={statusSuggestion.suggestedStatusId ?? undefined} />
            <span>に変更しますか？</span>
          </div>
        </div>
      </AppDialog>
    </>
  )
}
